/**
 * Snusify cart form: every product form in the theme - the buy row on each
 * product card and the buy block on the product page.
 *
 * Without this script the form posts to /cart/add and the visitor lands on
 * the cart. With it:
 *
 * - the add button follows the chosen pack, and a pack that earns a volume
 *   discount is priced the way the cart will price it, from the
 *   volume-pricing app's own offers (snippets/snusify-volume-pricing)
 * - pack sizes the volume-pricing app overwrites are put back just before
 *   the form is read
 * - above a card's add button, what the chosen pack saves
 * - adding stays on the page: the header's cart link, and any sections named
 *   in data-sections, are re-rendered from the same cart response, and the
 *   cart's new contents (sections/snusify-cart-state) bring every card's
 *   "in your cart" line, pack stock and every shipping box up to date
 * - the result is announced, in the form's own status line when it has one,
 *   otherwise in the added panel the header section carries, which can undo
 *   the add and closes by itself: two seconds after it shows, held open
 *   while the pointer or focus is on it, and at once at a click beside it
 * - the cart drawer, when the page has one, comes back in the same response;
 *   it never opens on an add
 * - a `cart:item-added` event bubbles up with the rendered sections, so the
 *   page around the form can refresh what it owns
 */

/**
 * @typedef {Object} CartLine
 * @property {string} key
 * @property {number} product
 * @property {number} variant
 * @property {number} quantity
 * @property {number} price - the line's price after discounts, minor units
 * @property {string} title
 * @property {string} options
 * @property {string} image
 */

/**
 * @typedef {Object} CartState - what sections/snusify-cart-state prints
 * @property {number} count
 * @property {number} total - after discounts, minor units
 * @property {CartLine[]} lines
 */

/**
 * @typedef {Object} AddedDetail
 * @property {CartLine | null} line - the cart line the add went into
 * @property {string} pack - the chosen pack's words, e.g. "10 cans"
 * @property {number} quantity - how many were added
 * @property {number} [total] - the cart total after the add
 * @property {Element | null} trigger - the button that added
 */

/**
 * @typedef {Object} CartAddedDetail
 * @property {Object} item - the line item the cart returned
 * @property {Record<string, string>} sections - rendered HTML by section id
 * @property {number} quantity - how many were added
 */

const CART_LINK_SECTION = 'snusify-cart-link';
const CART_STATE_SECTION = 'snusify-cart-state';
const CART_DRAWER_SECTION = 'snusify-cart-drawer';
const ADDED_MS = 2400;
const TOAST_MS = 3600;
/** How long the added panel stays once nothing is holding it open. */
const ADDED_OPEN_MS = 2000;

class SnusifyCartForm extends HTMLElement {
  /** Recreated on every connect: a controller aborted by a disconnect
      cannot be reused, and listeners bound to it would silently never fire. */
  #abort = null;
  #busy = false;
  #timer = 0;
  /** The server-rendered prices - the button's, and the line narrow rows show
      instead - for packs with no discount. */
  #unitDefaults = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener(
      'change',
      (event) => {
        if (/** @type {Element} */ (event.target).matches('[data-pack-input]')) this.#syncTotal();
      },
      { signal }
    );
    // Capture runs before anything that reads the form on the way back up.
    this.addEventListener('submit', (event) => this.#restorePackValues(event), { capture: true, signal });
    this.addEventListener('submit', (event) => this.#onSubmit(event), { signal });

    this.refresh();
  }

  /**
   * Price the pack sizes against the volume offers, then bring the button in
   * line with the chosen one. Public: the product page calls it after it has
   * re-rendered the packs inside this form.
   */
  refresh() {
    const tiers = tiersFor();
    const unitPrice = Number(this.dataset.price);
    const format = volumeData()?.format;
    // A card's form carries the regular price of a product on sale: there
    // every pack shows how much comes off it in all, the sale and the pack's
    // volume discount together.
    const regular = Number(this.dataset.compareAt);
    const onSale = regular > unitPrice;
    let deals = 0;

    for (const input of this.querySelectorAll('[data-pack-input]')) {
      const label = input.closest('label');
      label?.querySelector('[data-deal]')?.remove();
      delete input.dataset.percent;
      delete input.dataset.saving;
      delete input.dataset.final;

      const qty = Number(input.dataset.qty);
      if (!format || !Number.isFinite(unitPrice)) continue;
      const percent = percentFor(tiers, qty);
      // A pack without a discount keeps the total the server printed, if any.
      if (!percent) {
        input.dataset.total ??= format(unitPrice * qty);
        if (onSale) {
          label?.append(dealBadge(cutPercent(regular * qty, unitPrice * qty)));
          deals += 1;
        }
        continue;
      }

      const price = priceFor(unitPrice, qty, percent);
      input.dataset.percent = String(percent);
      input.dataset.saving = format(price.line - price.final);
      input.dataset.final = String(price.final);
      input.dataset.total = format(price.final);
      input.dataset.unit = format(price.unit);
      input.dataset.unitWas = format(unitPrice);

      // The price of one can in this pack, where the pack shows it.
      const unit = label?.querySelector('[data-pack-unit]');
      if (unit) unit.textContent = format(price.unit);
      label?.append(dealBadge(onSale ? cutPercent(regular * qty, price.final) : percent));
      deals += 1;
    }

    // Lets a row make room for the discount inside its boxes.
    for (const group of this.querySelectorAll('fieldset')) group.classList.toggle('has-deals', deals > 0);

    this.#syncTotal();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#timer);
  }

  /**
   * Bring the add button in line with the chosen pack: its price for one can
   * - discounted, with the list price struck, when the pack earns a volume
   * discount - and the saving over it. Narrow rows show the price on the
   * line above instead, which follows the same way.
   */
  #syncTotal() {
    const pack = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-pack-input]:checked'));
    const save = this.querySelector('[data-pack-save]');
    const words = volumeData()?.strings ?? {};
    if (!pack) {
      if (save) save.textContent = '';
      return;
    }

    // What the chosen pack saves, over the button; nothing for a pack that
    // earns no discount.
    if (save) {
      const line = pack.dataset.saving && words.saveAmount ? fill(words.saveAmount, { amount: pack.dataset.saving }) : '';
      // Rewriting the same words would announce them again.
      if (save.textContent !== line) save.textContent = line;
    }

    // A stacked card's button, and the product page's, carry what the chosen
    // pack costs in all. Elsewhere the price of one can, as the server prints
    // it, discounted where the pack earns it.
    if (this.matches('.snusify-qbuy--stack, .snusify-buy__cart-form') && this.#syncPackPrice(pack, words)) return;

    const units = [...this.querySelectorAll('[data-cart-unit]')];
    this.#unitDefaults ??= units.map((unit) => unit.innerHTML);
    for (const [index, unit] of units.entries()) {
      if (!pack.dataset.percent) {
        unit.innerHTML = this.#unitDefaults[index];
        continue;
      }
      unit.replaceChildren(
        text('span', 'visually-hidden', words.salePrice ?? ''),
        text('span', 'snusify-qbuy__now', pack.dataset.unit ?? '')
      );
    }
  }

  /**
   * What the chosen pack costs in all - thirty cans' price, not one can's -
   * after its volume discount; and where the pack or a sale cuts it, what the
   * same cans cost at the regular price, struck through. Read out with the
   * pack's own words ("30 cans").
   * @param {HTMLInputElement} pack
   * @param {Record<string, string>} words
   * @returns {boolean} whether there was a formatter to price it with
   */
  #syncPackPrice(pack, words) {
    const format = volumeData()?.format;
    if (!format) return false;
    const qty = Number(pack.dataset.qty ?? 1);
    const unitPrice = Number(this.dataset.price);
    const regular = Number(this.dataset.compareAt);
    const paid = pack.dataset.final ? Number(pack.dataset.final) : unitPrice * qty;
    const full = (regular > unitPrice ? regular : unitPrice) * qty;
    const cut = full > paid;
    const said = cut ? words.salePrice : words.regularPrice;
    const parts = [
      text('span', 'visually-hidden', `${pack.dataset.label ?? ''}, ${said ?? ''}`),
      text('span', 'snusify-qbuy__now', format(paid)),
    ];
    if (cut) parts.push(text('span', 'visually-hidden', words.regularPrice ?? ''), text('s', 'snusify-qbuy__was', format(full)));
    for (const unit of this.querySelectorAll('[data-cart-unit]')) unit.replaceChildren(...parts.map((node) => node.cloneNode(true)));
    return true;
  }

  /**
   * Bring the packs and the stock line in line with what the cart now holds
   * of this form's variant: a pack bigger than what is left is disabled, and
   * the choice moves to one that fits. Only a variant whose stock Shopify
   * counts carries data-stock; the rest have no limit to follow.
   * @param {number} inCart - how many of this variant the cart holds
   */
  syncStock(inCart) {
    if (!this.dataset.stock) return;
    const left = Number(this.dataset.stock) - inCart;
    const inputs = [...this.querySelectorAll('[data-pack-input]')];
    let allFit = true;
    for (const input of inputs) {
      const fits = Number(input.dataset.qty) <= left;
      input.disabled = !fits;
      if (!fits) allFit = false;
      const short = input.closest('label')?.querySelector('[data-pack-short]');
      if (short) short.hidden = fits;
    }

    const fitting = inputs.filter((input) => !input.disabled);
    const checked = inputs.find((input) => input.checked);
    if (fitting.length && (!checked || checked.disabled)) fitting[0].checked = true;
    const add = this.querySelector('[data-cart-add]');
    if (add) add.disabled = fitting.length === 0;

    const words = volumeData()?.strings ?? {};
    const stock = this.querySelector('[data-qbuy-stock]');
    if (stock) {
      stock.hidden = allFit;
      // The sentence is the theme's own translation, with its number in
      // bold; only a count is filled in.
      if (!allFit && left > 0) stock.innerHTML = fill(words.onlyLeft ?? '', { count: Math.trunc(left) });
      if (!allFit && left <= 0) stock.textContent = words.stockInCart ?? '';
    }
    this.#syncNote();
    this.#syncTotal();
  }

  /**
   * Show how many of this product the cart holds.
   * @param {number} count
   */
  showInCart(count) {
    const line = this.querySelector('[data-in-cart]');
    if (!line) return;
    line.hidden = count < 1;
    // The number on its own, for rows too narrow for the sentence.
    line.dataset.count = String(count);
    const text = line.querySelector('[data-in-cart-text]');
    if (text && count > 0) text.textContent = fill(volumeData()?.strings?.inCart ?? '', { count });
    this.#syncNote();
  }

  /** The note above the row shows while its stock line has something to say. */
  #syncNote() {
    const note = this.querySelector('[data-qbuy-note]');
    if (!note) return;
    note.hidden = [...note.children].every((child) => /** @type {HTMLElement} */ (child).hidden);
  }

  /**
   * The store's volume-pricing app sets every [name="quantity"] on the page
   * to its own default a few seconds after load. On radios that rewrites
   * each one's value, so whichever pack is checked would post the app's
   * number. Put the real sizes back.
   * @param {SubmitEvent} event
   */
  #restorePackValues(event) {
    const form = /** @type {HTMLFormElement} */ (event.target);
    for (const input of form.querySelectorAll('[data-pack-input]')) {
      input.value = input.dataset.qty ?? input.value;
    }
  }

  /** @param {SubmitEvent} event */
  async #onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    if (this.#busy) return;

    const add = form.querySelector('[data-cart-add]');
    const body = new FormData(form);
    const extra = (this.dataset.sections ?? '').split(',').map((id) => id.trim()).filter(Boolean);
    body.append('sections', [CART_LINK_SECTION, CART_STATE_SECTION, ...drawerSections(), ...extra].join(','));
    body.append('sections_url', window.location.pathname + window.location.search);

    this.#busy = true;
    add?.setAttribute('aria-busy', 'true');
    this.#clearInline();

    try {
      const response = await fetch(`${window.routes?.cart_add_url ?? '/cart/add'}.js`, {
        method: 'POST',
        body,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.#abort?.signal,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.#report(data.description || data.message || strings().error, true);
        // A refusal can still change the cart - "only 2 were added" - so
        // bring the header's count up to date anyway.
        refetchCartLinks();
        return;
      }

      // Read the pack before the cart's new state can move the choice.
      const quantity = Number(body.get('quantity') ?? 1);
      const pack = /** @type {HTMLInputElement | null} */ (form.querySelector('[data-pack-input]:checked'));
      const packLabel = pack?.dataset.label ?? '';

      refreshCartLinks(data.sections);
      const state = readCartState(data.sections);
      if (state) applyCartState(state);
      refreshShippingBoxes(data.sections);

      this.#report(strings().added, false, {
        line: state?.lines.find((line) => line.key === data.key) ?? null,
        pack: packLabel,
        quantity,
        total: state?.total,
        trigger: add,
      });
      this.#markAdded(add);

      /** @type {CustomEvent<CartAddedDetail>} */
      const addedEvent = new CustomEvent('cart:item-added', {
        bubbles: true,
        detail: { item: data, sections: data.sections ?? {}, quantity },
      });
      this.dispatchEvent(addedEvent);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#report(strings().error, true);
    } finally {
      this.#busy = false;
      // The page may have re-rendered the button in the meantime.
      for (const button of this.querySelectorAll('[data-cart-add][aria-busy]')) button.removeAttribute('aria-busy');
    }
  }

  /** @param {Element | null} button */
  #markAdded(button) {
    if (!button) return;
    button.classList.add('is-added');
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => button.classList.remove('is-added'), ADDED_MS);
  }

  #clearInline() {
    const error = this.querySelector('[data-cart-error]');
    if (error) error.hidden = true;
  }

  /**
   * Announce the outcome where the form keeps its own status line. Without
   * one - every card - a failure goes to the site toast and an add to the
   * added panel.
   * @param {string} message
   * @param {boolean} failed
   * @param {AddedDetail} [detail]
   */
  #report(message, failed, detail) {
    const status = this.querySelector('[data-cart-status]');
    const error = this.querySelector('[data-cart-error]');
    const link = this.querySelector('[data-cart-link]');

    if (!status && !error) {
      if (failed || !detail || !showAdded(message, detail)) toast(message, failed);
      return;
    }

    if (failed && error) {
      error.textContent = message;
      error.hidden = false;
      return;
    }

    announce(status, message);
    if (link) link.hidden = false;
  }
}

/* ---------- Volume pricing ---------- */

/**
 * @typedef {Object} VolumeTier
 * @property {number} quantity - the least number of items the tier needs
 * @property {number} percent - its discount on the line, in percent
 * @property {boolean} exact - the tier applies to exactly its quantity only
 */

/** @type {{ bundles: Object[], country: string, b2b: boolean, strings: Record<string, string>, format: (cents: number) => string } | null | undefined} */
let volumeCache;

/** The offers snippets/snusify-volume-pricing printed, parsed once per page. */
function volumeData() {
  if (volumeCache !== undefined) return volumeCache;
  volumeCache = null;
  const el = document.getElementById('SnusifyVolumePricing');
  if (!el) return null;
  try {
    const raw = JSON.parse(el.textContent ?? '');
    const bundles = [];
    for (const payload of raw.bundles ?? []) {
      try {
        bundles.push(typeof payload === 'string' ? JSON.parse(payload) : payload);
      } catch {
        // A payload we cannot read offers nothing.
      }
    }
    volumeCache = { ...raw, bundles, format: moneyFormatter(raw.moneySample) };
  } catch {
    volumeCache = null;
  }
  return volumeCache;
}

/**
 * Whether a bundle, as the app published it, applies on this page. Anything
 * this does not recognise counts as no: the page may then under-state a
 * saving, but it never promises one the checkout would not give.
 * @param {Object} bundle
 * @param {NonNullable<typeof volumeCache>} data
 */
function bundleApplies(bundle, data) {
  if (!bundle || bundle.bundle_type !== 'quantity_breaks') return false;
  if (!bundle.is_published || Number(bundle.is_archived) || bundle.deleted_at) return false;

  const now = Date.now();
  if (bundle.start_date_time && Date.parse(bundle.start_date_time) > now) return false;
  if (bundle.end_date_time && Date.parse(bundle.end_date_time) < now) return false;

  const markets = bundle.target_markets;
  if (Array.isArray(markets) && markets.length > 0) {
    const country = String(data.country ?? '').toUpperCase();
    if (!markets.some((market) => String(market).toUpperCase() === country)) return false;
  }
  if (bundle.exclude_b2b_customer && data.b2b) return false;

  // Bundles aimed at chosen products or collections are not confirmed from
  // the storefront here, so they show no discount.
  return bundle.visibility === 'all-products';
}

/** @returns {VolumeTier[]} every percentage tier that applies on this page */
function tiersFor() {
  const data = volumeData();
  if (!data) return [];
  const tiers = [];
  for (const bundle of data.bundles) {
    if (!bundleApplies(bundle, data)) continue;
    for (const offer of bundle.offers ?? []) {
      const quantity = Number(offer.quantity);
      const percent = Number(offer.discount_value);
      if (offer.discount_type !== 'percentage' || !(quantity > 0) || !(percent > 0) || percent >= 100) continue;
      tiers.push({ quantity, percent, exact: !bundle.volume_discount_enabled });
    }
  }
  return tiers;
}

/**
 * The best discount a quantity earns.
 * @param {VolumeTier[]} tiers
 * @param {number} qty
 */
function percentFor(tiers, qty) {
  let best = 0;
  for (const tier of tiers) {
    const reached = tier.exact ? qty === tier.quantity : qty >= tier.quantity;
    if (reached) best = Math.max(best, tier.percent);
  }
  return best;
}

/**
 * Price a line the way the cart does: the discount is rounded on the whole
 * line, and the cart shows the price for one rounded up from what is left.
 * @param {number} unitCents
 * @param {number} qty
 * @param {number} percent
 */
function priceFor(unitCents, qty, percent) {
  const line = unitCents * qty;
  const final = line - Math.round((line * percent) / 100);
  return { line, final, unit: Math.ceil(final / qty) };
}

/**
 * How far a price comes below the regular one, in whole percent: what a
 * card's pack badges say.
 * @param {number} regular - the regular price, minor units
 * @param {number} paid - what is paid, minor units
 * @returns {number}
 */
function cutPercent(regular, paid) {
  return regular > 0 ? Math.round((1 - paid / regular) * 100) : 0;
}

/**
 * A formatter that writes amounts the way the money filter does in the
 * visitor's currency, learned from one amount the server formatted:
 * 1234567 in minor units, e.g. "€12.345,67" or "12 345,67 kr".
 * @param {string | undefined} sample
 * @returns {(cents: number) => string}
 */
function moneyFormatter(sample) {
  const match = /\d[\d.,'\u00a0\u202f ]*\d/.exec(sample ?? '');
  if (!sample || !match) return (cents) => (cents / 100).toFixed(2);

  const digits = match[0];
  const prefix = sample.slice(0, match.index);
  const suffix = sample.slice(match.index + digits.length);
  // 1234567 is 12345.67: a mark before the last two digits is the decimal one.
  const decimal = /(\D)(\d{2})$/.exec(digits);
  const whole = decimal ? digits.slice(0, -3) : digits;
  const group = /\d(\D)\d{3}$/.exec(whole)?.[1] ?? '';

  return (cents) => {
    const fixed = decimal ? (cents / 100).toFixed(2) : String(Math.round(cents / 100));
    const [int, frac] = fixed.split('.');
    return prefix + int.replace(/\B(?=(\d{3})+(?!\d))/g, group) + (frac ? decimal[1] + frac : '') + suffix;
  };
}

/**
 * The badge on a pack that earns a discount: "Save 10%", in the theme's own
 * words (sections.snusify.save_percent), and its short form "−10%"
 * (sections.snusify.hero_volume_value), which a phone's narrow card shows
 * instead (snippets/snusify-quick-buy-styles). Screen readers always hear
 * the words.
 * @param {number} percent
 */
function dealBadge(percent) {
  const strings = volumeData()?.strings;
  const filled = (template) => template.replace('[percent]', String(percent));
  const badge = document.createElement('span');
  badge.className = 'snusify-deal';
  badge.dataset.deal = '';
  const short = text('span', 'snusify-deal__short', filled(strings?.saveShort ?? '−[percent]%'));
  short.setAttribute('aria-hidden', 'true');
  badge.append(text('span', 'snusify-deal__words', filled(strings?.save ?? '[percent]%')), short);
  return badge;
}

/**
 * @param {string} tag
 * @param {string} className
 * @param {string} content
 */
function text(tag, className, content) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  el.textContent = content;
  return el;
}

/* ---------- Shared helpers ---------- */

/** The cart drawer's section, when the page has a drawer to keep up. */
function drawerSections() {
  return document.querySelector('snusify-cart-drawer') ? [CART_DRAWER_SECTION] : [];
}

/**
 * Strings are printed once, on the site toast, so they stay translatable
 * without repeating on every card.
 * @returns {{ added: string, error: string }}
 */
function strings() {
  const toastEl = /** @type {HTMLElement | null} */ (document.querySelector('[data-snusify-toast]'));
  return {
    added: toastEl?.dataset.added ?? '',
    error: toastEl?.dataset.error ?? window.cartStrings?.error ?? '',
  };
}

/**
 * Swap the fresh cart link from a cart response into the header.
 * @param {Record<string, string> | undefined} sections
 */
function refreshCartLinks(sections) {
  const html = sections?.[CART_LINK_SECTION];
  if (!html) return;
  const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-snusify-cart-link]');
  if (!fresh) return;
  for (const link of document.querySelectorAll('[data-snusify-cart-link]')) link.replaceWith(fresh.cloneNode(true));
}

/** Ask the Section Rendering API for the cart link on its own. */
async function refetchCartLinks() {
  try {
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('sections', CART_LINK_SECTION);
    const response = await fetch(url);
    if (!response.ok) return;
    refreshCartLinks(await response.json());
  } catch {
    // The count catches up on the next page load.
  }
}

/**
 * Clear then set, so the same message twice in a row is still announced.
 * @param {Element | null} region
 * @param {string} message
 */
function announce(region, message) {
  if (!region) return;
  region.textContent = '';
  requestAnimationFrame(() => {
    region.textContent = message;
  });
}

let toastTimer = 0;

/**
 * Show the site toast. The visible toast is not itself a live region - it
 * appears and disappears - so the message is announced through the hidden
 * status line beside it, which is always in the page.
 * @param {string} message
 * @param {boolean} failed
 */
function toast(message, failed) {
  const el = /** @type {HTMLElement | null} */ (document.querySelector('[data-snusify-toast]'));
  announce(document.querySelector('[data-snusify-announce]'), message);
  if (!el) return;

  const text = el.querySelector('[data-toast-text]');
  if (text) text.textContent = message;
  el.classList.toggle('is-error', failed);
  el.hidden = false;
  requestAnimationFrame(() => el.classList.add('is-visible'));

  const hide = () => {
    if (el.matches(':hover, :focus-within')) {
      toastTimer = window.setTimeout(hide, 1200);
      return;
    }
    el.classList.remove('is-visible');
    toastTimer = window.setTimeout(() => {
      el.hidden = true;
    }, 300);
  };
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(hide, TOAST_MS);
}

/**
 * Fill a sentence's [placeholders], which the theme prints into its
 * translations so the words stay in the locale files.
 * @param {string} template
 * @param {Record<string, string | number>} values
 */
function fill(template, values) {
  return template.replace(/\[(\w+)\]/g, (match, name) => (name in values ? String(values[name]) : match));
}

/**
 * @param {ParentNode} root
 * @param {string} selector
 * @param {string} value
 */
function setText(root, selector, value) {
  const el = root.querySelector(selector);
  if (el) el.textContent = value;
}

/* ---------- The cart as it now is ---------- */

/**
 * @param {Record<string, string> | undefined} sections
 * @returns {CartState | null}
 */
function readCartState(sections) {
  const html = sections?.[CART_STATE_SECTION];
  if (!html) return null;
  const script = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-snusify-cart-state]');
  try {
    return JSON.parse(script?.textContent ?? '');
  } catch {
    return null;
  }
}

/**
 * Bring the page in line with the cart: the product page's "in your cart"
 * line and every card's pack stock.
 * @param {CartState} state
 */
function applyCartState(state) {
  const byProduct = new Map();
  const byVariant = new Map();
  for (const line of state.lines) {
    byProduct.set(line.product, (byProduct.get(line.product) ?? 0) + line.quantity);
    byVariant.set(line.variant, (byVariant.get(line.variant) ?? 0) + line.quantity);
  }

  for (const form of document.querySelectorAll('snusify-cart-form')) {
    if (!(form instanceof SnusifyCartForm)) continue;
    form.showInCart(byProduct.get(Number(form.dataset.productId)) ?? 0);
    const variant = Number(/** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'))?.value);
    form.syncStock(byVariant.get(variant) ?? 0);
  }
}

/* ---------- The added panel ---------- */

/** @type {{ key: string, previous: number, trigger: Element | null, openedAt: number } | null} */
let added = null;
let addedWired = false;
let addedTimer = 0;
let addedCloseTimer = 0;

/** @returns {HTMLElement | null} */
function addedPanel() {
  return document.querySelector('[data-snusify-added]');
}

/**
 * Show what an add from a card did. Returns false when there is no panel or
 * no line to show, so the caller can fall back to the toast.
 * @param {string} message
 * @param {AddedDetail} detail
 */
function showAdded(message, detail) {
  const panel = addedPanel();
  const { line } = detail;
  if (!panel || !line) return false;

  const format = volumeData()?.format;
  const words = volumeData()?.strings ?? {};
  // The packshot is decoration beside the name, so it carries no alt text.
  const media = panel.querySelector('[data-added-image]');
  if (media instanceof HTMLElement) {
    media.hidden = !line.image;
    if (line.image) {
      const image = document.createElement('img');
      image.src = line.image;
      image.alt = '';
      image.width = 56;
      image.height = 56;
      media.replaceChildren(image);
    }
  }
  setText(panel, '[data-added-name]', line.options ? `${line.title} (${line.options})` : line.title);
  setText(panel, '[data-added-pack]', detail.pack);
  setText(panel, '[data-added-line]', format ? fill(words.inCartTotal ?? '', { count: line.quantity, total: format(line.price) }) : '');

  added = {
    key: line.key,
    previous: Math.max(0, line.quantity - detail.quantity),
    trigger: detail.trigger,
    openedAt: window.scrollY,
  };
  wireAdded(panel);

  // One message at a time: a toast still up from before gives way.
  const toastEl = document.querySelector('[data-snusify-toast]');
  if (toastEl instanceof HTMLElement) {
    toastEl.classList.remove('is-visible');
    toastEl.hidden = true;
  }

  announce(document.querySelector('[data-snusify-announce]'), message);
  clearTimeout(addedTimer);
  panel.hidden = false;
  requestAnimationFrame(() => panel.classList.add('is-visible'));
  closeAddedSoon();
  return true;
}

/**
 * Close the added panel in a moment, unless the pointer or focus is on it
 * by then; leaving it starts the count again.
 */
function closeAddedSoon() {
  clearTimeout(addedCloseTimer);
  addedCloseTimer = window.setTimeout(() => {
    const panel = addedPanel();
    if (!panel || panel.hidden || panel.matches(':hover, :focus-within')) return;
    hideAdded(false);
  }, ADDED_OPEN_MS);
}

/**
 * @param {boolean} returnFocus - put focus back on the button that added,
 *   when it was inside the panel
 */
function hideAdded(returnFocus) {
  const panel = addedPanel();
  if (!panel || panel.hidden) return;
  const hadFocus = panel.contains(document.activeElement);
  panel.classList.remove('is-visible');
  clearTimeout(addedCloseTimer);
  clearTimeout(addedTimer);
  addedTimer = window.setTimeout(() => {
    panel.hidden = true;
  }, 250);

  const trigger = added?.trigger;
  if ((returnFocus || hadFocus) && trigger instanceof HTMLElement && trigger.isConnected) {
    trigger.focus({ preventScroll: true });
  }
}

/** Close, undo, Escape, a click beside it, the timer and scrolling on: set
    up once, on first use. */
function wireAdded(panel) {
  if (addedWired) return;
  addedWired = true;

  panel.querySelector('[data-added-close]')?.addEventListener('click', () => hideAdded(true));
  panel.querySelector('[data-added-undo]')?.addEventListener('click', () => undoAdded(panel));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) hideAdded(true);
  });
  // Pointing at it or focusing into it holds it open; leaving starts the
  // two seconds again.
  panel.addEventListener('pointerenter', () => clearTimeout(addedCloseTimer));
  panel.addEventListener('pointerleave', () => {
    if (!panel.hidden) closeAddedSoon();
  });
  panel.addEventListener('focusin', () => clearTimeout(addedCloseTimer));
  panel.addEventListener('focusout', (event) => {
    if (!panel.hidden && !panel.contains(/** @type {Node | null} */ (event.relatedTarget))) closeAddedSoon();
  });
  // A press anywhere beside it closes it at once.
  document.addEventListener('pointerdown', (event) => {
    if (panel.hidden || panel.contains(/** @type {Node} */ (event.target))) return;
    hideAdded(false);
  });
  // Scrolling on means shopping on: the panel steps aside once the page has
  // moved most of a screen, unless the visitor is using it.
  window.addEventListener(
    'scroll',
    () => {
      if (panel.hidden || !added || panel.matches(':hover, :focus-within')) return;
      if (Math.abs(window.scrollY - added.openedAt) > window.innerHeight * 0.8) hideAdded(false);
    },
    { passive: true }
  );
}

/**
 * Take back exactly what was just added: the line goes back to the quantity
 * it had before.
 * @param {HTMLElement} panel
 */
async function undoAdded(panel) {
  if (!added) return;
  const undo = /** @type {HTMLButtonElement | null} */ (panel.querySelector('[data-added-undo]'));
  if (undo) undo.disabled = true;

  try {
    const response = await fetch(`${window.routes?.cart_change_url ?? '/cart/change'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: added.key,
        quantity: added.previous,
        sections: [CART_LINK_SECTION, CART_STATE_SECTION, ...drawerSections()].join(','),
        sections_url: window.location.pathname + window.location.search,
      }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message);

    // The header's cart link, every card and the drawer follow from these.
    document.dispatchEvent(new CustomEvent('snusify:cart-changed', { detail: { sections: data.sections } }));
    hideAdded(true);
    added = null;
    toast(volumeData()?.strings?.removed ?? '', false);
  } catch {
    toast(strings().error, true);
  } finally {
    if (undo) undo.disabled = false;
  }
}

/* ---------- Shipping ---------- */

/**
 * The shipping box (snippets/snusify-shipping-box). Everything it says is
 * worked out by the server, so after a cart change it takes the fresh copy
 * of its context from sections/snusify-cart-state. It keeps its identity, so
 * the bar's fill can slide from where it was to where it now is.
 */
class SnusifyShippingBox extends HTMLElement {
  /** @param {Element} fresh - the same context's box, freshly rendered */
  take(fresh) {
    const before = /** @type {HTMLElement | null} */ (this.querySelector('[data-ship-fill]'))?.style.inlineSize;

    for (const name of this.getAttributeNames()) {
      if (!fresh.hasAttribute(name)) this.removeAttribute(name);
    }
    for (const attr of fresh.attributes) this.setAttribute(attr.name, attr.value);
    this.innerHTML = fresh.innerHTML;

    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-ship-fill]'));
    if (!bar || !before) return;
    const after = bar.style.inlineSize;
    if (before === after) return;
    bar.style.inlineSize = before;
    bar.getBoundingClientRect();
    bar.style.inlineSize = after;
  }
}

if (!customElements.get('snusify-shipping-box')) {
  customElements.define('snusify-shipping-box', SnusifyShippingBox);
}

/**
 * Every shipping box on the page takes the copy of its context that came
 * back with the cart (sections/snusify-cart-state). The drawer's and the
 * cart page's come back with their own sections as well; this brings them
 * the same thing.
 * @param {Record<string, string> | undefined} sections
 */
function refreshShippingBoxes(sections) {
  const html = sections?.[CART_STATE_SECTION];
  if (!html) return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const box of document.querySelectorAll('snusify-shipping-box')) {
    if (!(box instanceof SnusifyShippingBox)) continue;
    const template = doc.querySelector(`template[data-snusify-ship="${box.dataset.context}"]`);
    const fresh = template instanceof HTMLTemplateElement ? template.content.querySelector('snusify-shipping-box') : null;
    if (fresh) box.take(fresh);
  }
}

// A pick in a shipping box's country list sends its form. form.submit()
// rather than requestSubmit(): the away script listens for the pick itself
// (snippets/snusify-ship-away).
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-ship-country]') || !select.value) return;
  select.form?.submit();
});

// The drawer opening takes over from any notice still up.
document.addEventListener('snusify:cart-drawer-open', () => {
  hideAdded(false);
  const toastEl = document.querySelector('[data-snusify-toast]');
  if (toastEl instanceof HTMLElement && !toastEl.hidden) {
    clearTimeout(toastTimer);
    toastEl.classList.remove('is-visible');
    toastEl.hidden = true;
  }
});

// Other cart scripts - Dawn's cart page, the drawer - announce their changes
// with the same two sections, so the header's cart link and every card keep
// up.
document.addEventListener('snusify:cart-changed', (event) => {
  const sections = /** @type {CustomEvent} */ (event).detail?.sections;
  refreshCartLinks(sections);
  const state = readCartState(sections);
  if (state) applyCartState(state);
  refreshShippingBoxes(sections);
});

if (!customElements.get('snusify-cart-form')) {
  customElements.define('snusify-cart-form', SnusifyCartForm);
}

/** The cart line whose pack hint was just pressed, to take focus back once
    the cart has re-rendered it. */
let packHintFocus = null;
document.addEventListener('snusify:cart-changed', () => {
  packHintFocus = null;
});

/**
 * Under a cart line: how many more of it earn the next pack discount, as a
 * button that raises the line's quantity to it, or, at the top tier, that the
 * line has the best pack price. The tiers are the ones the cards price from,
 * and the rules count each product on its own, so the cart never promises a
 * saving the checkout would not give; with none that apply, nothing shows. A
 * tier beyond what the line can hold (data-max) is not offered.
 *
 * The button sets the quantity field Dawn's cart page already watches and
 * lets it do the update, as for any other change. The update re-renders the
 * line, and with it this element, which then takes the focus back.
 */
class SnusifyPackHint extends HTMLElement {
  connectedCallback() {
    const qty = Number(this.dataset.quantity);
    const max = this.dataset.max ? Number(this.dataset.max) : Infinity;
    const step = Number(this.dataset.step) || 1;
    const words = volumeData()?.strings ?? {};
    const tiers = tiersFor().filter((tier) => !tier.exact);
    const now = percentFor(tiers, qty);
    const next = tiers
      .filter((tier) => tier.quantity > qty && tier.quantity <= max && tier.quantity % step === 0 && tier.percent > now)
      .sort((a, b) => a.quantity - b.quantity)[0];
    const top = !tiers.some((tier) => tier.percent > now);

    this.replaceChildren();
    if (next && words.packNext) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'snusify-pack-hint__add';
      button.textContent = fill(words.packNext, { count: next.quantity - qty, percent: next.percent });
      // The visible words first, so the name still starts with what is read.
      if (this.dataset.title) {
        const whose = document.createElement('span');
        whose.className = 'visually-hidden';
        whose.textContent = `, ${this.dataset.title}`;
        button.append(whose);
      }
      button.addEventListener('click', () => this.#raiseTo(next.quantity));
      this.append(button);
    } else if (now && top && words.packBest) {
      this.textContent = fill(words.packBest, { percent: now });
    }
    this.hidden = !this.hasChildNodes();

    if (packHintFocus !== null && packHintFocus === this.dataset.line) {
      packHintFocus = null;
      this.#takeFocus();
    }
  }

  /**
   * After its own update: the next button, else the note, else the line
   * itself, which reads out its new quantity and price. Never the quantity
   * field, where a second Enter would submit the cart form to checkout.
   */
  #takeFocus() {
    let target = this.querySelector('button');
    if (!target && !this.hidden) target = this;
    target ??= this.closest('.cart-item');
    if (!(target instanceof HTMLElement)) return;
    if (!target.matches('button')) target.tabIndex = -1;
    target.focus({ preventScroll: true });
  }

  /** @param {number} quantity */
  #raiseTo(quantity) {
    const input = document.querySelector(`#Quantity-${CSS.escape(this.dataset.line ?? '')}`);
    if (!(input instanceof HTMLInputElement)) return;
    packHintFocus = this.dataset.line ?? null;
    input.value = String(quantity);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('snusify-pack-hint')) {
  customElements.define('snusify-pack-hint', SnusifyPackHint);
}

/*
 * The landing page's pack-discount figure (sections/snusify-hero-static):
 * the first tier the cart gives, from the same offers the cards price from.
 * The server holds its place with an invisible stand-in; without a tier the
 * place closes.
 */
for (const fact of document.querySelectorAll('[data-volume-fact]')) {
  const first = tiersFor()
    .filter((tier) => !tier.exact)
    .sort((a, b) => a.quantity - b.quantity)[0];
  const value = fact.querySelector('[data-volume-value]');
  const label = fact.querySelector('[data-volume-label]');
  if (!first || !(value instanceof HTMLElement) || !(label instanceof HTMLElement)) {
    fact.hidden = true;
    continue;
  }
  value.textContent = fill(value.dataset.volumeValue ?? '', { percent: first.percent });
  label.textContent = fill(label.dataset.volumeLabel ?? '', { count: first.quantity });
  fact.classList.remove('snusify-hero-static__fact--pending');
}
